import React from 'react';
import { Typography, Container, Button } from '@mui/material';
import { Link } from 'react-router-dom';
import ValuePropCard from '../shared-components/ValuePropCard';
import FeatureShowcase from '../shared-components/FeatureShowcase';
import HowItWorksSteps from '../shared-components/HowItWorksSteps';
import CTASection from '../shared-components/CTASection';
import TrustBadges from '../shared-components/TrustBadges';
import PlanPricingCards from 'app/shared-components/account/PlanPricingCards';
import {
  PersonAdd,
  GroupAdd,
  CheckCircleOutline,
  ConnectWithoutContact
} from '@mui/icons-material';

function Patients() {
  // Two Pathways
  const pathways = [
    {
      icon: <PersonAdd sx={{ fontSize: 40 }} />,
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      headline: "Your Provider Invites You",
      copy: "Receive a secure invitation from your mental health provider. Complete intake forms, share documents, and communicate directly—all in one place."
    },
    {
      icon: <GroupAdd sx={{ fontSize: 40 }} />,
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      headline: "Build Your Care Network",
      copy: "Take control by creating your own care team. Invite your therapist, psychiatrist, and other providers to collaborate on your care or that of a family member."
    }
  ];

  // Value Propositions as Feature Showcases
  const features = [
    {
      title: "Support Systems Welcome",
      description: "Mental health is a team effort. Securely invite parents, spouses, or caregivers to share updates and upload forms without needing full account access. Your support system can participate in care coordination while maintaining appropriate privacy boundaries.",
      stat: "Family-centered care",
      imagePosition: "right",
      imageFile: "assets/images/marketing/patients/family-care-team-screenshot.jpg"
    },
    {
      title: "Your Care Team Talks to Each Other",
      description: "Your therapist, psychiatrist, and other providers coordinate care in secure, shared spaces. Everyone stays on the same page about your treatment plan, medications, and progress. No more repeating your story to each provider.",
      stat: "Seamless coordination",
      imagePosition: "left",
      imageFile: "assets/images/marketing/providers/group-2-with-team-overlay.jpg"
    },
    {
      title: "Frictionless Access",
      description: "Book appointments online 24/7, complete intake forms from any device, and communicate securely with your care team. Get verified (to keep your information private and secure), and manage payments automatically. Mental healthcare without the administrative burden.",
      stat: "Care on your terms",
      imagePosition: "right",
      imageFile: "assets/images/marketing/patients/public-booking.jpg"
    }
  ];

  // How It Works Steps
  const steps = [
    {
      step: 1,
      title: "Get Invited OR Create Team",
      description: "Join when your provider invites you, or take the lead by creating your own care team and inviting your providers.",
      icon: <GroupAdd sx={{ fontSize: 40 }} />
    },
    {
      step: 2,
      title: "Complete Your Intake",
      description: "Fill out forms, upload insurance information, and provide your health history—all from your device.",
      icon: <CheckCircleOutline sx={{ fontSize: 40 }} />
    },
    {
      step: 3,
      title: "Stay Connected",
      description: "Message your providers, complete homework assignments, and track your progress throughout treatment.",
      icon: <ConnectWithoutContact sx={{ fontSize: 40 }} />
    }
  ];

  return (
    <div className="min-h-screen">
      {/* HERO SECTION */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-blue-50 via-white to-white">
        <Container maxWidth="lg">
          <div className="text-center max-w-4xl mx-auto">
            <Typography
              variant="h1"
              align="center"
              className="text-4xl lg:text-6xl font-extrabold text-gray-900 mb-6 leading-tight"
            >
              Better Mental Health Care Starts with{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                Connection
              </span>
            </Typography>

            <Typography
              variant="h2"
              align="center"
              className="text-lg lg:text-xl text-gray-600 mb-12 font-normal leading-relaxed"
            >
              Clear Communication for a Healthier Tomorrow. Navigate the complexities of mental health with confidence. Halespring provides a secure, HIPAA-compliant space designed for true partnership with your mental healthcare providers. Our collaboration features make it easy to share information and communicate across your entire care team.
            </Typography>

            <div className="flex flex-col sm:flex-row gap-4 mb-12 justify-center">
              <Button
                component={Link}
                to="/user-sign-up"
                variant="contained"
                size="large"
                className="bg-blue-600 hover:bg-blue-700 px-8 py-3.5 text-base font-bold shadow-lg shadow-blue-500/30 transition-all hover:-translate-y-0.5"
              >
                Get Started Free
              </Button>
              <Button
                component={Link}
                to="/public/search"
                variant="outlined"
                size="large"
                className="border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 px-8 py-3.5 text-base font-semibold"
              >
                Find a Provider
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 opacity-80">
              <TrustBadges />
            </div>
          </div>
        </Container>
      </section>

      {/* TWO PATHWAYS SECTION */}
      <section className="py-24 bg-white shadow-inner">
        <Container maxWidth="lg">
          <div className="text-center mb-16 w-full max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Two Ways to Get Started
            </Typography>
            <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
              Whether your provider invites you or you create your own care team, Halespring makes coordination simple.
            </Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {pathways.map((pathway, index) => (
              <ValuePropCard
                key={index}
                icon={
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto ${pathway.iconBg} ${pathway.iconColor}`}>
                    {pathway.icon}
                  </div>
                }
                headline={pathway.headline}
                copy={pathway.copy}
              />
            ))}
          </div>
        </Container>
      </section>

      {/* VALUE PROPOSITIONS AS FEATURE SHOWCASES */}
      <section className="py-24 bg-gray-50">
        <Container maxWidth="lg">
          <div className="text-center mb-20 w-full max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Why Families Choose Halespring
            </Typography>
            <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed">
              Secure, private, and designed for the way mental healthcare should work.
            </Typography>
          </div>

          <div className="space-y-36">
            {features.map((feature, index) => (
              <FeatureShowcase
                key={index}
                title={feature.title}
                description={feature.description}
                stat={feature.stat}
                imagePosition={feature.imagePosition}
                imageFile={feature.imageFile}
              />
            ))}
          </div>
        </Container>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-32 bg-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(#3b82f6 1px, transparent 1px)', backgroundSize: '32px 32px' }}></div>

        <Container maxWidth="lg" className="relative z-10">
          <div className="text-center mb-24">
            <Typography variant="h2" className="text-4xl font-bold text-gray-900 mb-6">
              How It Works
            </Typography>
            <Typography variant="body1" className="text-xl text-gray-600 max-w-2xl mx-auto">
              Get started in three simple steps.
            </Typography>
          </div>

          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 border-t-2 border-dashed border-blue-200 -z-10" />

            {steps.map((item, index) => (
              <div key={index} className="flex flex-col items-center text-center group">
                <div className="w-24 h-24 bg-white rounded-full shadow-lg border-4 border-blue-100 flex items-center justify-center text-blue-600 mb-20 transition-transform group-hover:scale-110 duration-300 z-10 relative">
                  {item.icon}
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm shadow-md">
                    {item.step}
                  </div>
                </div>

                <div className="bg-white p-8 rounded-2xl shadow-sm border border-blue-100 h-full w-full hover:shadow-md transition-shadow flex flex-col justify-center items-center text-center min-h-[200px]">
                  <Typography variant="h5" className="font-bold text-gray-900 mb-4">
                    {item.title}
                  </Typography>
                  <Typography variant="body1" className="text-gray-600 leading-relaxed">
                    {item.description}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      {/* PRICING FOR PATIENTS */}
      <section className="py-24 bg-white">
        <Container maxWidth="lg">
          <div className="text-center mb-16 w-full max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Simple, Transparent Pricing
            </Typography>
            <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed mb-8">
              Work with unlimited providers, and/or create one family care team at no cost; perfect for most patients and families. Managing care for multiple family members? Upgrade to create unlimited care teams.
            </Typography>
          </div>

          <PlanPricingCards type="user" isPublic signUpURL="/user-sign-up" />
        </Container>
      </section>

      {/* PRIVACY & SECURITY */}
      <section className="py-24 bg-gray-50">
        <Container maxWidth="lg">
          <div className="text-center max-w-3xl mx-auto">
            <Typography variant="h2" align="center" className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Your Privacy is Our Priority
            </Typography>
            <Typography variant="body1" align="center" className="text-xl text-gray-600 leading-relaxed mb-8">
              Halespring is built from the ground up to be HIPAA-compliant. Your personal health information is encrypted, secure, and never sold to third parties.
            </Typography>
            <div className="flex items-center justify-center gap-8 opacity-90">
              <TrustBadges />
            </div>
          </div>
        </Container>
      </section>

      {/* FINAL CTA SECTION */}
      <CTASection
        headline="Ready to Take Control of Your Care?"
        copy="Join the patients and families using Halespring to coordinate better mental healthcare."
        primaryCTA="Get Started Free"
        primaryCTALink="/user-sign-up"
        secondaryCTA="Find a Provider"
        secondaryCTALink="/public/search"
        trustElements={[
          "Free forever for one care team",
          "No credit card required",
          "HIPAA compliant",
          "Cancel anytime"
        ]}
      />
    </div>
  );
}

export default Patients;